import { WebLayout } from 'components/Layout';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { HeroCarousel } from '../components/HeroCarousel';

const slideLeft = {
  hidden: { opacity: 0, x: -100 },
  visible: { opacity: 1, x: 0 },
};
const slideRight = {
  hidden: { opacity: 0, x: 100 },
  visible: { opacity: 1, x: 0 },
};

// A glowing animated divider
const EnergyDivider = () => (
  <div className="relative my-16">
    {/* <div className="h-1 w-full bg-gradient-to-r from-transparent via-[#F7931E] to-transparent animate-pulse blur-[2px]" /> */}
    <span className="block mt-2 h-[3px] w-24 bg-gradient-to-r from-[#F7931E] to-transparent"></span>
  </div>
);

// Section with parallax
const ParallaxSection = ({ children, speed = 30 }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], [-speed, speed]);

  return (
    <motion.section ref={ref} style={{ y }} className="relative z-10 py-12">
      {children}
    </motion.section>
  );
};

export function Landing() {
  return (
    <WebLayout headerContent={<HeroCarousel />}>
      <ParallaxSection>
        <motion.div
          variants={slideLeft}
          initial="hidden"
          whileInView="visible"
          transition={{ duration: 1 }}
        >
          <h2 className="text-[#F7931E] font-bold text-4xl sm:text-5xl">
            <em>Trading LPG made possible</em>
          </h2>
          <p className="mt-4 text-lg text-gray-300">
            Forget Wall Street jargon. Forget pipelines and tankers.
          </p>
          <p className="mt-2 text-gray-400">
            Your gateway to the $200B+ LPG market — simple, accessible, and without the gatekeepers.
          </p>
          <p className="mt-2 text-gray-400">
            Every stake represents a fraction of actual LPG reserves. Trade and build your energy
            portfolio.
          </p>
        </motion.div>
      </ParallaxSection>

      <EnergyDivider />

      {/* Why Now Section */}
      <ParallaxSection speed={50}>
        <motion.div
          variants={slideRight}
          initial="hidden"
          whileInView="visible"
          transition={{ duration: 1 }}
        >
          <h2 className="text-[#F7931E] font-bold text-4xl sm:text-5xl">
            <em>Why Now?</em>
          </h2>
          <p className="mt-4 text-lg text-gray-300">
            Energy is the new gold. Global demand is skyrocketing, and investors are rushing to
            secure positions.
          </p>
          <p className="mt-2 text-gray-400">
            By entering early, you position yourself in a booming market before it becomes
            saturated.
          </p>
        </motion.div>
      </ParallaxSection>

      <EnergyDivider />

      {/* New Section: Why Invest in Virtual Gas */}
      <ParallaxSection>
        <motion.div
          variants={slideLeft}
          initial="hidden"
          whileInView="visible"
          transition={{ duration: 1 }}
        >
          <h2 className="text-[#F7931E] font-bold text-4xl sm:text-5xl">
            <em>Why Invest in Virtual Gas?</em>
          </h2>
          <ul className="mt-6 space-y-4 text-gray-300 text-lg list-disc list-inside">
            <li>Tap into a $200B+ energy market with fractional access.</li>
            <li>Hedge against inflation with a tangible, high-demand commodity.</li>
            <li>Trade seamlessly, but backed by actual reserves.</li>
            <li>Be an early participant in the first wave of virtual energy trading.</li>
          </ul>
        </motion.div>
      </ParallaxSection>

      <EnergyDivider />

      {/* New Section: How It Works */}
      <ParallaxSection speed={40}>
        <motion.div
          variants={slideRight}
          initial="hidden"
          whileInView="visible"
          transition={{ duration: 1 }}
        >
          <h2 className="text-[#F7931E] font-bold text-4xl sm:text-5xl">
            <em>How It Works</em>
          </h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-8 text-center text-gray-300">
            {[
              {
                step: '01',
                title: 'Sign Up',
                desc: 'Join the platform as an early user or investor.',
              },
              {
                step: '02',
                title: 'Buy Stakes',
                desc: 'Purchase fractional gas reserves backed by real assets.',
              },
              {
                step: '03',
                title: 'Trade & Grow',
                desc: 'Sell, trade, and diversify your portfolio effortlessly.',
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                whileHover={{ scale: 1.05 }}
                className="bg-[#111]/50 p-6 rounded-xl border border-[#F7931E]/20 shadow-lg"
              >
                <div className="text-4xl text-[#F7931E] font-bold">{item.step}</div>
                <div className="mt-4 text-xl font-semibold">{item.title}</div>
                <p className="mt-2 text-gray-400">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </ParallaxSection>

      <EnergyDivider />

      {/* New Section: Early Adopter Benefits */}
      <ParallaxSection>
        <motion.div
          variants={slideLeft}
          initial="hidden"
          whileInView="visible"
          transition={{ duration: 1 }}
        >
          <h2 className="text-[#F7931E] font-bold text-4xl sm:text-5xl">
            <em>Early Adopter Benefits</em>
          </h2>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6 text-gray-300">
            {[
              'Exclusive early-bird trading fees & bonuses.',
              'Priority access to future energy-backed assets.',
              'Founding member recognition and rewards program.',
            ].map((benefit, i) => (
              <motion.div
                key={i}
                whileHover={{ scale: 1.05 }}
                className="p-6 bg-[#111]/50 rounded-lg border border-[#F7931E]/20 shadow-md"
              >
                {benefit}
              </motion.div>
            ))}
          </div>
        </motion.div>
      </ParallaxSection>
    </WebLayout>
  );
}
